import React, { ReactNode, useState, useRef, useEffect, forwardRef } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { AppDialogFrame } from '../../overlays/AppDialogFrame';

// --- BUTTONS ---

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    icon?: React.ElementType;
    isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({ 
    children, 
    variant = 'primary', 
    icon: Icon, 
    className = '', 
    isLoading,
    ...props 
}) => {
    const baseStyles = "flex items-center justify-center gap-2 rounded-xl text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed";
    
    const variants = {
        primary: "bg-primary text-white hover:bg-primary-hover shadow-lg shadow-primary/20 py-2.5 px-6",
        secondary: "bg-primary/5 text-primary hover:text-primary-hover border border-primary/10 hover:bg-primary/10 py-2.5 px-6",
        ghost: "text-irongrey/60 hover:text-irongrey hover:bg-gray-100 py-2 px-4",
        danger: "bg-strawberry/10 text-strawberry hover:bg-strawberry/20 py-2.5 px-6"
    };

    return (
        <button className={`${baseStyles} ${variants[variant]} ${className}`} {...props}>
            {isLoading ? <span className="animate-spin">⌛</span> : Icon && <Icon size={18} />}
            {children}
        </button>
    );
};

// --- CARDS ---

export const Card: React.FC<{ children: ReactNode; className?: string; onClick?: () => void }> = ({ children, className = '', onClick }) => (
    <div 
        onClick={onClick}
        className={`bg-white rounded-2xl p-6 shadow-card border border-gray-100 group ${onClick ? 'cursor-pointer hover:shadow-soft transition-shadow' : ''} ${className}`}
    >
        {children}
    </div>
);

// --- INPUTS & FORMS ---

export const InputLabel: React.FC<{ children: ReactNode }> = ({ children }) => (
    <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-1.5 block transition-colors duration-500 group-hover:text-irongrey/90">
        {children}
    </label>
);

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className = '', ...props }, ref) => (
    <input 
        ref={ref}
        className={`w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary placeholder:text-irongrey/30 transition-all ${className}`}
        {...props}
    />
));
Input.displayName = 'Input';

interface SelectOption {
    label: string;
    value: string;
}

interface SelectProps {
    value?: string;
    onChange?: (value: string) => void;
    options: (SelectOption | string)[];
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    searchable?: boolean;
}

export const Select: React.FC<SelectProps> = ({ 
    value, 
    onChange, 
    options,
    placeholder = 'Select...', 
    className = '',
    disabled = false,
    searchable = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const normalizedOptions: SelectOption[] = options.map(opt => 
        typeof opt === 'string' ? { label: opt, value: opt } : opt
    );

    const filteredOptions = normalizedOptions.filter(opt => 
        !searchTerm.trim() || opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedOption = normalizedOptions.find(opt => opt.value === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && searchable) {
            searchInputRef.current?.focus();
        }
    }, [isOpen, searchable]);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const handleSelect = (val: string) => {
        if (onChange) onChange(val);
        setIsOpen(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
            event.preventDefault();
            setIsOpen((current) => !current);
        }
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <div 
                onClick={() => !disabled && setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={disabled ? -1 : 0}
                className={`w-full bg-gray-50 border ${isOpen ? 'border-primary ring-1 ring-primary' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm font-semibold text-irongrey flex items-center justify-between cursor-pointer transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50'}`}
            >
                <span className={selectedOption ? 'text-irongrey' : 'text-irongrey/30'}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown size={16} className={`text-irongrey/40 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-xl z-50 max-h-60 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-100">
                    {searchable && (
                        <div className="p-2 border-b border-gray-50 bg-white sticky top-0 z-10">
                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-irongrey/30" />
                                <input
                                    ref={searchInputRef}
                                    className="w-full bg-gray-50 border border-gray-100 rounded-lg pl-9 pr-3 py-2 text-xs font-semibold text-irongrey focus:outline-none focus:ring-1 focus:ring-primary/50"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    )}
                    <div className="p-1 overflow-y-auto">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt) => (
                                <div
                                    key={opt.value}
                                    onClick={() => handleSelect(opt.value)}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-between cursor-pointer transition-colors ${value === opt.value ? 'bg-primary/10 text-primary' : 'hover:bg-gray-50 text-irongrey'}`}
                                >
                                    {opt.label}
                                    {value === opt.value && <Check size={14} />}
                                </div>
                            ))
                        ) : (
                            <div className="px-3 py-4 text-center text-xs text-irongrey/40">No options found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- BADGES ---

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    let styles = "bg-gray-100 text-irongrey/60";
    
    switch (status.toLowerCase()) {
        case 'verified':
        case 'accepted':
        case 'enrolled':
            styles = "bg-jungle/10 text-jungle";
            break;
        case 'pending':
        case 'in-progress':
            styles = "bg-primary/10 text-primary";
            break;
        case 'urgent':
        case 'declined':
        case 'denied':
            styles = "bg-strawberry/10 text-strawberry";
            break;
        case 'waitlisted':
            styles = "bg-amber-100 text-amber-600";
            break;
    }

    return (
        <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide ${styles}`}>
            {status}
        </span>
    );
};

// --- MODALS ---

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    children: ReactNode;
    footer?: ReactNode;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, description, children, footer }) => {
    return (
        <AppDialogFrame
            open={isOpen}
            onClose={onClose}
            title={title}
            description={description}
            footer={footer}
            maxWidthClassName="max-w-lg"
            bodyClassName="space-y-6"
            bodyPaddingClassName="px-8 py-6"
            footerPaddingClassName="px-8 py-5"
            titleClassName="text-xl"
            descriptionClassName="text-xs font-medium text-irongrey/40"
        >
            {children}
        </AppDialogFrame>
    );
};

// --- HEADER ---

export const PageHeader: React.FC<{ title: string; actions?: ReactNode }> = ({ title, actions }) => (
    <header className="flex items-center justify-between px-8 py-6 bg-white border-b border-gray-100 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-irongrey tracking-tight">{title}</h1>
        <div className="flex items-center gap-3">
            {actions}
        </div>
    </header>
);

// --- EXAMPLE USAGE COMPONENT ---
export const DesignSystemExample = () => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectValue, setSelectValue] = React.useState('');

    return (
        <div className="p-10 space-y-10 bg-background-light min-h-screen">
            <section>
                <InputLabel>Buttons</InputLabel>
                <div className="flex gap-4 mt-2">
                    <Button variant="primary" onClick={() => setIsModalOpen(true)}>Open Modal</Button>
                    <Button variant="secondary">Secondary</Button>
                    <Button variant="ghost">Ghost</Button>
                    <Button variant="danger">Danger</Button>
                </div>
            </section>

            <section>
                <InputLabel>Cards & Badges</InputLabel>
                <div className="grid grid-cols-3 gap-4 mt-2">
                    <Card>
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="font-bold text-irongrey">Applicant Card</h3>
                            <StatusBadge status="Accepted" />
                        </div>
                        <p className="text-sm text-irongrey/60">Content goes here.</p>
                    </Card>
                    <Card>
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="font-bold text-irongrey">Pending Item</h3>
                            <StatusBadge status="Pending" />
                        </div>
                    </Card>
                </div>
            </section>

            <section className="max-w-md">
                <InputLabel>Form Elements</InputLabel>
                <div className="space-y-4 mt-2">
                    <div>
                        <InputLabel>Full Name</InputLabel>
                        <Input placeholder="e.g. John Doe" />
                    </div>
                    <div>
                        <InputLabel>Status</InputLabel>
                        <Select 
                            options={['Option 1', 'Option 2', 'Option 3']}
                            value={selectValue}
                            onChange={setSelectValue}
                        />
                    </div>
                </div>
            </section>

            <Modal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)}
                title="Example Modal"
                description="This uses the Design System modal component."
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                        <Button onClick={() => setIsModalOpen(false)}>Confirm Action</Button>
                    </>
                }
            >
                <p className="text-sm text-irongrey">
                    This is the modal body content. It uses the standard padding and typography defined in the system.
                </p>
            </Modal>
        </div>
    );
};
